'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { ComponentProps, useMemo } from 'react';
import { fetchActiveOrders } from '@/lib/api/history/client';
import { ACTIVE_ORDERS_QK } from '@/lib/api/history/hooks';
import type { ActiveOrderPageResponse } from '@/lib/api/history/models';
import { useAuthContext } from '@/lib/auth/AuthProvider';
import { CentText } from '@/lib/components/Text/CentText';
import { PercentText } from '@/lib/components/Text/PercentText';
import { TranslatedText } from '@/lib/components/Text/TranslatedText';
import { USDText } from '@/lib/components/Text/USDText';
import { useTradingBalances } from '@/lib/components/Trading/TradingDataContext';
import { useApiTranslation } from '@/lib/hooks/useApiTranslation';
import { useOrderFormStore, useSelectedOutcome } from '@/lib/store/order-form-store';
import { useOutcomePrices } from '@/lib/store/prices-store';
import { EMarketAction } from '@/lib/types/market-action';
import { MarketItem } from '@/lib/types/markets/market';
import { Icon } from '@/lib/ui-kit/icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/lib/ui-kit/tooltip';
import { UiImage } from '@/lib/ui-kit/ui-image';
import { cn } from '@/lib/ui-kit/utils';
import { OutcomeTag } from '@/lib/ui-kit-v2/outcome-tag';
import { YesNoButton } from '@/lib/ui-kit-v2/yes-no-button';
import { formatCents, formatMoney } from '@/lib/utils/formatAmount';
import { getYesLegPrice, isOutcomeActive, outcomeFallbackPrices } from '@/lib/utils/outcomePrice';

type OutcomeCardProps = {
  outcome: MarketItem;
  showBadges?: boolean;
  isEventConcluded?: boolean;
} & ComponentProps<'div'>;

export function OutcomeCard({
  outcome,
  showBadges = false,
  isEventConcluded = false,
  className,
  ref,
  ...props
}: OutcomeCardProps) {
  const t = useTranslations('common');
  const { tr } = useApiTranslation();
  const translatedTitle = tr(outcome.translations, 'question', outcome.groupItemTitle);
  const isPaused = outcome.status === 'PAUSED';
  const isResolved = outcome.status === 'RESOLVED';
  const isAwaitingResolution = outcome.status === 'AWAITING_RESOLUTION';
  // Trading is over for good: "Trading closed" instead of the Yes/No buttons.
  // A paused outcome keeps them (disabled), trading may resume.
  const isTradingClosed = isAwaitingResolution || (isEventConcluded && !isResolved);
  const isNoLongerPossible =
    isResolved && (outcome.r ?? []).length > 0 && outcome.r!.every((r) => r.isWin === false);
  const winningResolution = isResolved ? outcome.r?.find((r) => r.isWin === true) : null;
  // Tradable only while both the event and the outcome are open: an ended event
  // keeps its outcomes ACTIVE.
  const isDisabled = isEventConcluded || !isOutcomeActive(outcome);
  // Trading stopped, so the book behind the store is empty: show the price
  // event-service stored at the close instead (0 when it has none).
  const closingPrice = getYesLegPrice(outcome) ?? 0;

  const setIsMobileFormOpened = useOrderFormStore.use.setIsMobileFormOpened();

  const setSelectedOutcomeId = useOrderFormStore.use.setSelectedOutcomeId();
  const setIntent = useOrderFormStore.use.setIntent();
  const selectedAction = useOrderFormStore.use.action();
  const selectedIntent = useOrderFormStore.use.intent();
  const selectedOutcome = useSelectedOutcome();
  const storePrices = useOutcomePrices(outcome.id);
  // The store is hydrated in an effect, so it is empty in the server HTML and
  // on the first client render. Fall back to the prices carried by the outcome
  // itself (order-book snapshot applied server-side) so crawlers and the first
  // paint show real numbers instead of 0% / 0¢.
  const fallbackPrices = useMemo(() => outcomeFallbackPrices(outcome), [outcome]);
  const livePrices = storePrices ?? fallbackPrices;

  const balancesData = useTradingBalances();
  const { isAuthenticated } = useAuthContext();
  const { data: activeOrdersData } = useQuery<ActiveOrderPageResponse>({
    queryKey: [ACTIVE_ORDERS_QK, outcome.id],
    queryFn: () => fetchActiveOrders({ eventOutcomeId: outcome.id, sort: ['asc'] }),
    enabled: isAuthenticated && showBadges,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const buyYes = useMemo(() => {
    if (isDisabled) return closingPrice;
    if (selectedAction === EMarketAction.BUY) {
      if (typeof livePrices?.bestAsk === 'number') return livePrices.bestAsk;
      return 0;
    }
    if (typeof livePrices?.bestBid === 'number') return livePrices.bestBid;
    return 0;
  }, [isDisabled, closingPrice, livePrices, selectedAction]);

  const buyNo = useMemo(() => {
    if (isDisabled) return 1 - closingPrice;
    if (selectedAction === EMarketAction.BUY) {
      if (typeof livePrices?.bestBid === 'number') return 1 - livePrices.bestBid;
      return 0;
    }
    if (typeof livePrices?.bestAsk === 'number') return 1 - livePrices.bestAsk;
    return 0;
  }, [isDisabled, closingPrice, livePrices, selectedAction]);

  const chance = useMemo(() => {
    if (isDisabled) return closingPrice;
    return livePrices?.chance != null && typeof livePrices.chance === 'number'
      ? livePrices.chance
      : 0;
  }, [isDisabled, closingPrice, livePrices]);

  const chanceIsUp = useMemo(() => {
    const delta = (livePrices as { chanceDelta?: number } | undefined)?.chanceDelta;
    if (typeof delta === 'number' && delta !== 0) return delta > 0;
    return null;
  }, [livePrices]);

  const badgeData = useMemo(() => {
    if (!showBadges) return null;

    const resolutions = outcome.r;
    const yesResolution = resolutions?.find((r) => r.isPositive === true);
    const noResolution = resolutions?.find((r) => r.isPositive === false);
    const yesTokenId = yesResolution?.id;
    const noTokenId = noResolution?.id;

    const yesBalance = balancesData?.balances?.find((b) => b.eventTokenId === yesTokenId);
    const noBalance = balancesData?.balances?.find((b) => b.eventTokenId === noTokenId);

    const yesShares = yesBalance?.value ?? 0;
    const noShares = noBalance?.value ?? 0;

    const yesOpenPrice = yesBalance?.averageOpenPrice ?? 0;
    const noOpenPrice = noBalance?.averageOpenPrice ?? 0;

    const ordersCount = activeOrdersData?.items?.length ?? 0;

    return { yesShares, noShares, yesOpenPrice, noOpenPrice, ordersCount };
  }, [showBadges, outcome.r, balancesData, activeOrdersData]);

  const isCurrentOutcomeSelected = outcome.id === selectedOutcome?.id;
  // A disabled button never reads as picked (the store preselects Yes on load).
  const isYesActive = !isDisabled && isCurrentOutcomeSelected && selectedIntent === 'yes';
  const isNoActive = !isDisabled && isCurrentOutcomeSelected && selectedIntent === 'no';

  const handlePickYes = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDisabled) return;
    setIntent('yes');
    setSelectedOutcomeId(outcome.id);
    setIsMobileFormOpened(true);
  };

  const handlePickNo = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDisabled) return;
    setIntent('no');
    setSelectedOutcomeId(outcome.id);
    setIsMobileFormOpened(true);
  };

  return (
    <div
      ref={ref}
      className={cn(
        'relative flex cursor-pointer flex-col py-4 transition-colors hover:bg-v2-btn-secondary/40 min-[701px]:pr-4',
        className,
      )}
      {...props}
    >
      {/* Desktop layout (>700px) */}
      <div className="relative z-10 hidden min-[701px]:flex min-[701px]:flex-col min-[701px]:gap-2">
        <div className="grid grid-cols-[minmax(200px,280px)_1fr_288px] items-center gap-4">
          <div className="flex min-w-0 items-center gap-4">
            <div className="relative shrink-0">
              <UiImage
                size={44}
                alt={translatedTitle}
                src={outcome.image || '/evfallback.webp'}
                fallback="/evfallback.webp"
                className="rounded-[8px] border border-v2-border"
              />
              {isPaused && (
                <>
                  <div className="absolute inset-0 rounded-[8px] bg-[#080809]/70" />
                  <div className="absolute -top-1.5 -right-0.5 rounded-[8px] bg-v2-surface-primary p-0.5">
                    <Icon name="Freeze" size={16} />
                  </div>
                </>
              )}
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-[15px] font-semibold leading-[19px] text-v2-text-primary">
                {translatedTitle}
              </span>
              <span className="truncate text-[12px] font-normal leading-4 tracking-[-0.12px] text-v2-text-secondary">
                <USDText value={outcome.volume ?? 0} /> Vol.
              </span>
            </div>
          </div>

          <div
            className={cn(
              'flex items-center justify-center gap-0.5',
              isDisabled ? 'text-v2-text-secondary' : 'text-v2-text-primary',
            )}
          >
            {/* A live sliver reads "<1%"; a closed outcome shows its exact price (0% for a
                resolved loser). */}
            {!isDisabled && chance < 0.01 ? (
              <span className="text-[24px] leading-[32px] font-semibold">{'<1%'}</span>
            ) : (
              <PercentText value={chance} className="text-[24px] leading-[32px] font-semibold" />
            )}
            {chanceIsUp !== null && (
              <Icon
                name="ArrowUp2"
                size={20}
                className={cn('shrink-0', chanceIsUp ? '' : 'rotate-180')}
              />
            )}
          </div>

          <div className="flex items-center justify-end">
            {isTradingClosed ? (
              <span className="text-right text-[15px] font-semibold text-v2-text-secondary">
                {t('tradingClosed')}
              </span>
            ) : isResolved ? (
              isNoLongerPossible ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1">
                      <span className="text-right text-[15px] font-semibold text-v2-text-secondary">
                        {t('noLongerPossible')}
                      </span>
                      <Icon name="Info" size={16} className="text-v2-text-secondary" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[300px] rounded-[10px] bg-v2-surface-secondary px-4 py-3">
                    <span className="text-[13px] text-v2-text-primary">{t('resolvedTooltip')}</span>
                  </TooltipContent>
                </Tooltip>
              ) : winningResolution ? (
                <div className="flex items-center gap-1">
                  <span className="text-[15px] font-semibold text-v2-text-primary">
                    {winningResolution.isPositive ? t('yes') : t('no')}
                  </span>
                  <Icon
                    name={winningResolution.isPositive ? 'CheckCircle' : 'CloseCircle'}
                    size={16}
                    className={winningResolution.isPositive ? 'text-v2-green' : 'text-red-400'}
                  />
                </div>
              ) : null
            ) : (
              <div className="flex w-full items-center gap-2">
                <YesNoButton
                  variant="yes"
                  active={isYesActive}
                  disabled={isDisabled}
                  onClick={handlePickYes}
                  className="flex-1"
                >
                  <TranslatedText token="common.yes" /> <CentText value={buyYes} />
                </YesNoButton>
                <YesNoButton
                  variant="no"
                  active={isNoActive}
                  disabled={isDisabled}
                  onClick={handlePickNo}
                  className="flex-1"
                >
                  <TranslatedText token="common.no" /> <CentText value={buyNo} />
                </YesNoButton>
              </div>
            )}
          </div>
        </div>

        {badgeData &&
          (badgeData.yesShares > 0 || badgeData.noShares > 0 || badgeData.ordersCount > 0) && (
            <div className="flex flex-wrap items-center gap-2">
              {badgeData.yesShares > 0 && (
                <OutcomeTag variant="yes" size="s">
                  {formatMoney(badgeData.yesShares, { decimals: 2 })} • {t('yes')}{' '}
                  {formatCents(badgeData.yesOpenPrice * 100)}
                </OutcomeTag>
              )}
              {badgeData.noShares > 0 && (
                <OutcomeTag variant="no" size="s">
                  {formatMoney(badgeData.noShares, { decimals: 2 })} • {t('no')}{' '}
                  {formatCents(badgeData.noOpenPrice * 100)}
                </OutcomeTag>
              )}
              {badgeData.ordersCount > 0 && (
                <OutcomeTag variant="order" size="s">
                  {t('openOrders', { count: badgeData.ordersCount })}
                </OutcomeTag>
              )}
            </div>
          )}
      </div>

      {/* Mobile layout (≤700px) */}
      <div className="relative z-10 flex flex-col gap-4 max-[700px]:flex min-[701px]:hidden">
        <div className="grid grid-cols-[1fr_auto] gap-2">
          <div className="flex min-w-0 flex-col gap-1">
            <span className="text-[15px] font-semibold leading-[19px] text-v2-text-primary">
              {translatedTitle}
            </span>
            <span className="truncate text-[12px] font-normal leading-4 tracking-[-0.12px] text-v2-text-secondary">
              <USDText value={outcome.volume ?? 0} /> Vol.
            </span>
          </div>
          <div
            className={cn(
              'flex items-center gap-0.5',
              isDisabled ? 'text-v2-text-secondary' : 'text-v2-text-primary',
            )}
          >
            {!isDisabled && chance < 0.01 ? (
              <span className="text-[18px] leading-6 font-semibold">{'<1%'}</span>
            ) : (
              <PercentText value={chance} className="text-[18px] leading-6 font-semibold" />
            )}
            {chanceIsUp !== null && (
              <Icon
                name="ArrowUp2"
                size={16}
                className={cn('shrink-0', chanceIsUp ? '' : 'rotate-180')}
              />
            )}
          </div>
        </div>

        {badgeData &&
          (badgeData.yesShares > 0 || badgeData.noShares > 0 || badgeData.ordersCount > 0) && (
            <div className="flex flex-wrap items-center gap-2">
              {badgeData.yesShares > 0 && (
                <OutcomeTag variant="yes" size="s">
                  {formatMoney(badgeData.yesShares, { decimals: 2 })} • {t('yes')}{' '}
                  {formatCents(badgeData.yesOpenPrice * 100)}
                </OutcomeTag>
              )}
              {badgeData.noShares > 0 && (
                <OutcomeTag variant="no" size="s">
                  {formatMoney(badgeData.noShares, { decimals: 2 })} • {t('no')}{' '}
                  {formatCents(badgeData.noOpenPrice * 100)}
                </OutcomeTag>
              )}
              {badgeData.ordersCount > 0 && (
                <OutcomeTag variant="order" size="s">
                  {t('openOrders', { count: badgeData.ordersCount })}
                </OutcomeTag>
              )}
            </div>
          )}

        {isTradingClosed ? (
          <span className="text-right text-[15px] font-semibold text-v2-text-secondary">
            {t('tradingClosed')}
          </span>
        ) : isResolved ? (
          isNoLongerPossible ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center justify-end gap-1">
                  <span className="text-right text-[15px] font-semibold text-v2-text-secondary">
                    {t('noLongerPossible')}
                  </span>
                  <Icon name="Info" size={16} className="text-v2-text-secondary" />
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-[300px] rounded-[10px] bg-v2-surface-secondary px-4 py-3">
                <span className="text-[13px] text-v2-text-primary">{t('resolvedTooltip')}</span>
              </TooltipContent>
            </Tooltip>
          ) : winningResolution ? (
            <div className="flex items-center justify-end gap-1">
              <span className="text-[15px] font-semibold text-v2-text-primary">
                {winningResolution.isPositive ? t('yes') : t('no')}
              </span>
              <Icon
                name={winningResolution.isPositive ? 'CheckCircle' : 'CloseCircle'}
                size={16}
                className={winningResolution.isPositive ? 'text-v2-green' : 'text-red-400'}
              />
            </div>
          ) : null
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <YesNoButton
              variant="yes"
              active={isYesActive}
              disabled={isDisabled}
              onClick={handlePickYes}
              className="w-full"
            >
              <TranslatedText token="common.yes" /> <CentText value={buyYes} />
            </YesNoButton>
            <YesNoButton
              variant="no"
              active={isNoActive}
              disabled={isDisabled}
              onClick={handlePickNo}
              className="w-full"
            >
              <TranslatedText token="common.no" /> <CentText value={buyNo} />
            </YesNoButton>
          </div>
        )}
      </div>
    </div>
  );
}
